// Las características del producto: lo que no es una medida (material, capacidad, potencia,
// "Escote V"…). En memoria es una lista clave/valor con origen; se GUARDA en
// `variantes.atributos` (jsonb, lista de textos), el mismo lugar donde la IA deja las
// características al subir el producto: "Clave: valor", o el valor solo si no tiene clave
// (ADR-2026-10-14-las-caracteristicas-viven-en-atributos, que supersede la columna
// `productos.ficha` de ADR-2026-10-14-la-ficha-del-producto-es-una-columna-json). Las
// MEDIDAS quedan aparte, en `productos.medidas`: son números para calcular. Puro.
import { resumenMedidas, type Medidas } from './medidas';

export type OrigenDeFicha = 'usuario' | 'ia' | 'tiendanube';

export interface CampoDeFicha {
  /** Vacía = una característica suelta ("Glitter"): sólo el valor. */
  clave: string;
  valor: string;
  /** `usuario` = lo confirmó la persona y es fuente de verdad; el resto es revisable. */
  origen: OrigenDeFicha;
  /** ISO 8601. */
  actualizado: string;
}

export interface FichaDeProducto {
  campos: CampoDeFicha[];
}

export const MAX_CAMPOS_FICHA = 20;
export const MAX_LARGO_VALOR = 200;
const MAX_LARGO_CLAVE = 40;

const normalizarClave = (v: unknown): string =>
  typeof v === 'string' ? v.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, MAX_LARGO_CLAVE) : '';

const normalizarValor = (v: unknown): string =>
  typeof v === 'string'
    ? v.trim().replace(/\s+/g, ' ').slice(0, MAX_LARGO_VALOR)
    : typeof v === 'number' && Number.isFinite(v)
      ? String(v)
      : '';

const esOrigen = (v: unknown): v is OrigenDeFicha => v === 'usuario' || v === 'ia' || v === 'tiendanube';

/** Lo que vuelve de la base → una ficha saneada (null, basura o campos vacíos no rompen). */
/** Lo que identifica un campo: su clave, o su valor si es una característica suelta. */
const identidad = (clave: string, valor: string): string => clave || `=${valor.toLowerCase()}`;

export function normalizarFicha(raw: unknown): FichaDeProducto {
  const campos = (raw as { campos?: unknown } | null)?.campos;
  if (!Array.isArray(campos)) return { campos: [] };
  const vistos = new Set<string>();
  const out: CampoDeFicha[] = [];
  for (const c of campos) {
    const o = (c ?? {}) as Record<string, unknown>;
    const clave = normalizarClave(o.clave);
    const valor = normalizarValor(o.valor);
    if (!valor || vistos.has(identidad(clave, valor))) continue;
    vistos.add(identidad(clave, valor));
    out.push({
      clave,
      valor,
      origen: esOrigen(o.origen) ? o.origen : 'ia',
      actualizado: typeof o.actualizado === 'string' ? o.actualizado : new Date().toISOString(),
    });
    if (out.length >= MAX_CAMPOS_FICHA) break;
  }
  return { campos: out };
}

/**
 * Suma o reemplaza campos. Lo que confirmó la persona (`origen: 'usuario'`) no se pisa sin
 * `sobrescribir`: esas claves vuelven en `rechazados`, para que quien llama (el agente) pregunte.
 */
export function fusionarFicha(
  actual: FichaDeProducto,
  nuevos: Array<{ clave: string; valor: string }>,
  opts: { origen: OrigenDeFicha; sobrescribir: boolean; ahora?: string },
): { ficha: FichaDeProducto; rechazados: string[]; guardados: number; sinLugar: string[] } {
  const ahora = opts.ahora ?? new Date().toISOString();
  const campos = [...actual.campos];
  const rechazados: string[] = [];
  // Los que no entraron porque la ficha ya tiene el tope: antes se perdían callados y quien
  // llamaba los contaba como guardados (ADR-2026-10-19-revision-greptile-pdf-y-catalogo).
  const sinLugar: string[] = [];
  let guardados = 0;
  for (const n of nuevos) {
    const clave = normalizarClave(n.clave);
    const valor = normalizarValor(n.valor);
    if (!valor) continue;
    const i = campos.findIndex((c) => identidad(c.clave, c.valor) === identidad(clave, valor));
    // Una característica suelta que ya está no tiene nada que actualizar.
    if (i >= 0 && !clave) continue;
    if (i >= 0) {
      if (campos[i].origen === 'usuario' && !opts.sobrescribir) {
        rechazados.push(clave || valor);
        continue;
      }
      campos[i] = { clave, valor, origen: opts.origen, actualizado: ahora };
      guardados += 1;
    } else if (campos.length < MAX_CAMPOS_FICHA) {
      campos.push({ clave, valor, origen: opts.origen, actualizado: ahora });
      guardados += 1;
    } else {
      sinLugar.push(clave || valor);
    }
  }
  return { ficha: { campos }, rechazados, guardados, sinLugar };
}

/** Las medidas (si hay) y los campos en una línea: "Alto 90 cm · … · material: roble". Vacío si no hay nada. */
export function resumenFicha(ficha: FichaDeProducto, medidas: Medidas | null | undefined): string {
  const partes: string[] = [];
  const m = resumenMedidas(medidas);
  if (m) partes.push(m);
  for (const c of ficha.campos) partes.push(c.clave ? `${c.clave}: ${c.valor}` : c.valor);
  return partes.join(' · ');
}

const capitalizar = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);
/** "Potencia: 1200 W" → clave y valor; sin ":" (o con algo que no parece una clave), suelta. */
const LINEA_CON_CLAVE = /^([^:]{1,40}):\s*(.+)$/;

/** `variantes.atributos` → la ficha. Sin origen guardado: todo vuelve como `ia` (revisable). */
export function fichaDesdeAtributos(raw: unknown): FichaDeProducto {
  if (!Array.isArray(raw)) return { campos: [] };
  const campos = raw
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((linea) => {
      const m = linea.trim().match(LINEA_CON_CLAVE);
      return m ? { clave: m[1], valor: m[2] } : { clave: '', valor: linea };
    });
  return normalizarFicha({ campos });
}

/** La ficha → las líneas que se guardan en `variantes.atributos`. */
export function atributosDesdeFicha(ficha: FichaDeProducto): string[] {
  return ficha.campos.map((c) => (c.clave ? `${capitalizar(c.clave)}: ${c.valor}` : c.valor));
}
